import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {Box} from "@mui/material";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {surveyMarkdownToPlainText} from "../../shared/surveyMarkdownText.util";
import {RunnerQuestionProps} from "./runnerQuestion.types";
import ScaleLabels from "./ScaleLabels";
import {STAR_VALUES, STAR_ROW_WIDTH, starIconSx, starRatingRowSx} from "./starRatingLayout";
import {surveyControls} from "../surveyRunnerColors";

function StarsQuestion({question, localize, language, value, onAnswerChange}: RunnerQuestionProps) {
    const {t} = useTranslation(undefined, {lng: language});

    const [localRating, setLocalRating] = useState<number | undefined>(undefined);
    const [hovered, setHovered] = useState(0);

    const selected = value?.rateValue ?? localRating;

    const pick = (rating: number) => {
        setLocalRating(rating);
        onAnswerChange?.({...value, rateValue: rating});
    };

    const lowLabel = localize(question.lowLabel);
    const highLabel = localize(question.highLabel);

    return (
        <Box sx={{width: STAR_ROW_WIDTH, maxWidth: '100%'}}>
            <Box
                sx={starRatingRowSx}
                role={'group'}
                aria-label={surveyMarkdownToPlainText(localize(question.title)) || undefined}
                onMouseLeave={() => setHovered(0)}
            >
                {STAR_VALUES.map(star => {
                    /*
                        Hover and selection used to draw the identical solid star, so a guest
                        sweeping the row could not tell what they had already given from what they
                        were merely pointing at. Selection stays solid; the hover preview beyond it
                        is the same shape at half strength, which reads as "this is what you would
                        get" without ever claiming to be the answer.
                    */
                    const chosen = star <= (selected ?? 0);
                    const previewed = !chosen && star <= hovered;
                    const filled = chosen || previewed;

                    return (
                        <Box
                            key={star}
                            component={'button'}
                            type={'button'}
                            onClick={() => pick(star)}
                            onMouseEnter={() => setHovered(star)}
                            onFocus={() => setHovered(star)}
                            onBlur={() => setHovered(0)}
                            aria-label={t('{{n}} of {{max}} stars', {n: star, max: STAR_VALUES.length})}
                            aria-pressed={star === selected}
                            sx={{
                                p: 0,
                                minWidth: 0,
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                color: theme => surveyControls(theme).accent,
                                opacity: previewed ? 0.5 : 1,
                                transition: 'color 150ms ease, opacity 150ms ease',
                            }}
                        >
                            {/*
                                Both glyphs stay mounted and CSS decides which one shows. Swapping the icon
                                component instead remounted the svg while the pointer was down: the hover
                                state flushes between mousedown and mouseup, the pressed node leaves the
                                document and the browser never synthesises the click. A mouse settles its
                                hover long before pressing, but a phone tap fires the whole sequence at
                                once, so a guest on their phone could not pick a star at all.
                            */}
                            <StarIcon sx={{...starIconSx, display: filled ? 'inline-block' : 'none'}}/>
                            <StarBorderIcon sx={{...starIconSx, display: filled ? 'none' : 'inline-block'}}/>
                        </Box>
                    );
                })}
            </Box>

            <ScaleLabels lowLabel={lowLabel} highLabel={highLabel}/>
        </Box>
    );
}

export default StarsQuestion;
